import { useState, useEffect } from 'react';
import { FiX, FiClock } from 'react-icons/fi';
import { Modal } from '../../design-system';
import LoadingSpinner from '../common/LoadingSpinner';

const TIME_MODIFIER_TYPES = [
  { value: 'none', label: 'Sin modificación', description: 'Usa el tiempo base de la habitación' },
  { value: 'relative', label: 'Relativa (±minutos)', description: 'Suma o resta minutos al tiempo base' },
  { value: 'percentage', label: 'Porcentual (±%)', description: 'Suma o resta un porcentaje del tiempo base' },
  { value: 'absolute', label: 'Absoluta (fija)', description: 'Tiempo fijo, ignora el tiempo de la habitación' }
];

const CleaningTypeDialog = ({ cleaningType, onSave, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: cleaningType?.name || '',
    includes_sheet_change: cleaningType?.includes_sheet_change || false,
    includes_towel_change: cleaningType?.includes_towel_change || false,
    time_modifier_type: cleaningType?.time_modifier_type || 'none',
    time_modifier_value: cleaningType?.time_modifier_value || 0
  });
  const [errors, setErrors] = useState({});

  const isEditing = !!cleaningType;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dataToSend = {
        name: formData.name,
        includes_sheet_change: formData.includes_sheet_change,
        includes_towel_change: formData.includes_towel_change,
        time_modifier_type: formData.time_modifier_type,
        time_modifier_value: parseInt(formData.time_modifier_value) || 0
      };

      await onSave(dataToSend);
    } catch (error) {
      console.error('Error saving cleaning type:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="md"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Editar Tipo de Limpieza' : 'Nuevo Tipo de Limpieza'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-4" id="cleaning-type-form">
          {/* Name */}
          <div>
            <label htmlFor="name" className="form-label">
              Nombre del tipo de limpieza *
            </label>
            <input
              id="name"
              type="text"
              className={`form-input ${errors.name ? 'border-danger-500' : ''}`}
              placeholder="Ej: Limpieza Diaria, Limpieza Profunda, Check-out"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-danger-600">{errors.name}</p>
            )}
          </div>

          {/* Laundry options */}
          <div>
            <label className="form-label mb-3">
              Configuración de ropa de cama
            </label>
            <div className="space-y-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
              <label className="flex items-start space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.includes_sheet_change}
                  onChange={(e) => handleChange('includes_sheet_change', e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                    Incluye cambio de sábanas
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Se cambian las sábanas durante este tipo de limpieza
                  </p>
                </div>
              </label>

              <label className="flex items-start space-x-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={formData.includes_towel_change}
                  onChange={(e) => handleChange('includes_towel_change', e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                    Incluye cambio de toallas
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Se cambian las toallas durante este tipo de limpieza
                  </p>
                </div>
              </label>

              {!formData.includes_sheet_change && !formData.includes_towel_change && (
                <div className="flex items-start space-x-2 bg-yellow-50 border border-yellow-200 rounded-md p-3 mt-3">
                  <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-yellow-800">
                      Nota: Este tipo de limpieza no incluye cambio de ropa de cama
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      No se aplicarán costes de lavandería para este tipo
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Time modifier section */}
          <div>
            <label className="form-label mb-3 flex items-center gap-2">
              <FiClock className="text-gray-500" />
              Modificador de tiempo de limpieza
            </label>
            <div className="space-y-3 bg-gray-50 rounded-lg p-4 border border-gray-200">
              {/* Modifier type selector */}
              <div>
                <select
                  value={formData.time_modifier_type}
                  onChange={(e) => {
                    handleChange('time_modifier_type', e.target.value);
                    // Reset value when changing type
                    if (e.target.value === 'none') {
                      handleChange('time_modifier_value', 0);
                    }
                  }}
                  className="form-input w-full"
                >
                  {TIME_MODIFIER_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {TIME_MODIFIER_TYPES.find(t => t.value === formData.time_modifier_type)?.description}
                </p>
              </div>

              {/* Value input - only show if not 'none' */}
              {formData.time_modifier_type !== 'none' && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.time_modifier_type === 'percentage' ? 'Porcentaje' : 'Minutos'}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={formData.time_modifier_value}
                      onChange={(e) => handleChange('time_modifier_value', e.target.value)}
                      className="form-input w-32"
                      placeholder={formData.time_modifier_type === 'absolute' ? 'Ej: 15' : 'Ej: 5 o -5'}
                      min={formData.time_modifier_type === 'absolute' ? 1 : undefined}
                    />
                    <span className="text-sm text-gray-600">
                      {formData.time_modifier_type === 'percentage' ? '%' : 'min'}
                    </span>
                  </div>
                  
                  {/* Help text based on type */}
                  <p className="text-xs text-gray-500 mt-2">
                    {formData.time_modifier_type === 'relative' && (
                      <>Ejemplo: Si la habitación tiene 30 min y pones +5, el tiempo será 35 min</>
                    )}
                    {formData.time_modifier_type === 'percentage' && (
                      <>Ejemplo: Si la habitación tiene 30 min y pones +10%, el tiempo será 33 min</>
                    )}
                    {formData.time_modifier_type === 'absolute' && (
                      <>El tiempo de limpieza será siempre {formData.time_modifier_value || '?'} minutos, sin importar la configuración de la habitación</>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="cleaning-type-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </div>
          ) : (
            isEditing ? 'Actualizar' : 'Crear'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CleaningTypeDialog;
